import { Fragment, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '../components/icons.js';
import { TechAvatar, fmtDate, fmtTime, fmtMoney } from '../components/shell.js';
import {
  clients,
  estimates,
  invoices,
  jobNotes,
  jobs,
  techs,
  STATUS_FLOW,
  type JobStatus,
} from '../data/seed.js';

type Tab = 'details' | 'photos' | 'notes' | 'billing';

function StatusTimeline({
  current,
  onChange,
}: {
  current: JobStatus;
  onChange?: (s: JobStatus) => void;
}) {
  const flow = STATUS_FLOW;
  const idx = flow.indexOf(current);
  const cancelled = current === 'Cancelled';
  return (
    <div>
      <div className="timeline">
        {flow.map((s, i) => {
          const done = !cancelled && i < idx;
          const isCurrent = !cancelled && i === idx;
          return (
            <Fragment key={s}>
              <div
                className={
                  'timeline-step ' + (done ? 'done ' : '') + (isCurrent ? 'current' : '')
                }
                onClick={() => onChange && onChange(s)}
                style={{ cursor: onChange ? 'pointer' : 'default' }}
              >
                <div className="timeline-dot">{done ? Icon.check({ size: 12 }) : i + 1}</div>
                <div className="timeline-label">{s}</div>
              </div>
              {i < flow.length - 1 && <div className={'timeline-bar ' + (done ? 'done' : '')} />}
            </Fragment>
          );
        })}
      </div>
      {cancelled && (
        <div style={{ marginTop: 10 }}>
          <span className="pill cancel">
            <span className="dot" />
            Cancelled
          </span>
        </div>
      )}
    </div>
  );
}

export function JobDetailPage() {
  const navigate = useNavigate();
  const { id: jobId } = useParams<{ id: string }>();
  const job0 = jobs.find((j) => j.id === jobId);
  const [status, setStatus] = useState<JobStatus | null>(job0 ? job0.status : null);
  const [tab, setTab] = useState<Tab>('details');

  if (!job0 || !status)
    return (
      <div className="page">
        <div className="empty-state">Job not found.</div>
      </div>
    );

  const job = { ...job0, status };
  const client = clients.find((c) => c.id === job.clientId)!;
  const tech = techs.find((t) => t.id === job.techId);
  const addr = client.addresses[job.addressIdx]!;
  const notes = jobNotes[job.id] || [];
  const linkedInvoices = invoices.filter((i) => i.jobId === job.id);
  const linkedEstimates = estimates.filter((e) => e.jobId === job.id);

  return (
    <div className="page">
      <div style={{ marginBottom: 12 }}>
        <button className="btn ghost sm" onClick={() => navigate('/jobs')}>
          {Icon.arrowLeft({ size: 14 })} All jobs
        </button>
      </div>

      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div>
          <div
            className="mono"
            style={{ fontSize: 12.5, color: 'hsl(var(--muted-foreground))' }}
          >
            {job.id}
          </div>
          <h1
            style={{ margin: '4px 0 6px', fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em' }}
          >
            {job.appliance.type} repair · {client.name}
          </h1>
          <div
            style={{
              fontSize: 13,
              color: 'hsl(var(--muted-foreground))',
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {Icon.mapPin({ size: 12 })} {addr.line1}, {addr.city}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {Icon.clock({ size: 12 })} {fmtDate(job.scheduledStart)} ·{' '}
              {fmtTime(job.scheduledStart)}–{fmtTime(job.scheduledEnd)}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button className="btn">{Icon.phone({ size: 14 })} Call client</button>
          <button className="btn">{Icon.fileText({ size: 14 })} Estimate</button>
          <button className="btn primary">{Icon.receipt({ size: 14 })} Invoice</button>
        </div>
      </div>

      {/* Status timeline */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">
          <span>Status</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              className="btn ghost sm danger"
              onClick={() => setStatus('Cancelled')}
            >
              {Icon.x({ size: 12 })} Cancel
            </button>
          </div>
        </div>
        <StatusTimeline current={status} onChange={setStatus} />
        <div className="muted" style={{ fontSize: 11.5, marginTop: 10 }}>
          Click a step to advance status (demo only).
        </div>
      </div>

      <div className="tab-bar">
        <button
          className={'tab ' + (tab === 'details' ? 'active' : '')}
          onClick={() => setTab('details')}
        >
          Details
        </button>
        <button
          className={'tab ' + (tab === 'photos' ? 'active' : '')}
          onClick={() => setTab('photos')}
        >
          Photos ({job.photos})
        </button>
        <button
          className={'tab ' + (tab === 'notes' ? 'active' : '')}
          onClick={() => setTab('notes')}
        >
          Notes ({notes.length})
        </button>
        <button
          className={'tab ' + (tab === 'billing' ? 'active' : '')}
          onClick={() => setTab('billing')}
        >
          Billing
        </button>
      </div>

      {tab === 'details' && (
        <div className="grid-2">
          <div className="stack gap-4">
            <div className="card">
              <div className="card-title">
                <span>Appliance</span>
              </div>
              <div
                style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}
              >
                <div>
                  <div className="muted" style={{ fontSize: 11.5, marginBottom: 2 }}>
                    Type
                  </div>
                  {job.appliance.type}
                </div>
                <div>
                  <div className="muted" style={{ fontSize: 11.5, marginBottom: 2 }}>
                    Brand
                  </div>
                  {job.appliance.brand}
                </div>
                <div>
                  <div className="muted" style={{ fontSize: 11.5, marginBottom: 2 }}>
                    Model
                  </div>
                  <span className="mono" style={{ fontSize: 12 }}>
                    {job.appliance.model || '—'}
                  </span>
                </div>
                <div>
                  <div className="muted" style={{ fontSize: 11.5, marginBottom: 2 }}>
                    Serial
                  </div>
                  <span className="mono" style={{ fontSize: 12 }}>
                    {job.appliance.serial || '—'}
                  </span>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-title">
                <span>Problem description</span>
              </div>
              <div style={{ fontSize: 13.5, lineHeight: 1.55 }}>{job.problem}</div>
            </div>
            <div className="card">
              <div className="card-title">
                <span>Source</span>
              </div>
              <div style={{ fontSize: 13.5 }}>{job.source}</div>
              {job.sourceCompany && (
                <div className="muted" style={{ marginTop: 4, fontSize: 12.5 }}>
                  {job.sourceCompany}
                </div>
              )}
            </div>
          </div>
          <div className="stack gap-4">
            <div className="card">
              <div className="card-title">
                <span>Client</span>
              </div>
              <div
                onClick={() => navigate('/clients/' + client.id)}
                style={{ cursor: 'pointer' }}
                className="row"
              >
                <div className="avatar sm" style={{ background: 'hsl(var(--muted))' }}>
                  {client.name
                    .split(' ')
                    .map((w) => w[0])
                    .slice(0, 2)
                    .join('')
                    .toUpperCase()}
                </div>
                <div className="row-main">
                  <div className="row-title">{client.name}</div>
                  <div className="row-sub mono" style={{ fontSize: 11.5 }}>
                    {client.phone}
                  </div>
                </div>
                <div className="row-aside">{Icon.chevronRight({ size: 14 })}</div>
              </div>
            </div>
            <div className="card">
              <div className="card-title">
                <span>Assigned tech</span>
                <button className="btn ghost sm">Reassign</button>
              </div>
              {tech && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <TechAvatar tech={tech} size="md" />
                  <div>
                    <div style={{ fontWeight: 500 }}>{tech.name}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      Field tech
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="card">
              <div className="card-title">
                <span>Address</span>
                <button className="btn ghost sm">{Icon.mapPin({ size: 12 })} Directions</button>
              </div>
              <div style={{ fontSize: 13.5 }}>
                {addr.line1}
                {addr.line2 ? ', ' + addr.line2 : ''}
                <br />
                {addr.city}, {addr.state} {addr.zip}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'photos' && (
        <div className="card">
          <div className="card-title">
            <span>Photos</span>
            <button className="btn sm">{Icon.plus({ size: 12 })} Upload</button>
          </div>
          {job.photos > 0 ? (
            <div className="photo-grid">
              {Array.from({ length: job.photos }, (_, i) => (
                <div key={i} className="photo-thumb">
                  photo {i + 1}
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">No photos yet. Tap Upload to add.</div>
          )}
        </div>
      )}

      {tab === 'notes' && (
        <div className="card">
          <div className="card-title">
            <span>Notes</span>
          </div>
          <div className="stack gap-3" style={{ marginBottom: 12 }}>
            {notes.length === 0 && <div className="empty-state">No notes yet.</div>}
            {notes.map((n, i) => (
              <div key={i} className="note-item">
                <div className="meta">
                  <span style={{ fontWeight: 500, color: 'hsl(var(--foreground))' }}>{n.author}</span>
                  {' · '}
                  {new Date(n.timestamp).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                  })}
                </div>
                <div>{n.body}</div>
              </div>
            ))}
          </div>
          <textarea className="textarea" rows={3} placeholder="Add a note…" />
          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn primary sm">Add note</button>
          </div>
        </div>
      )}

      {tab === 'billing' && (
        <div className="grid-2">
          <div className="card">
            <div className="card-title">
              <span>Estimates</span>
              <button className="btn ghost sm">{Icon.plus({ size: 12 })} New</button>
            </div>
            <div className="row-list">
              {linkedEstimates.map((e) => (
                <div key={e.id} className="row">
                  <div className="mono" style={{ fontSize: 12 }}>
                    {e.id}
                  </div>
                  <div className="row-main">
                    <div className="row-title">{fmtMoney(e.total)}</div>
                    <div className="row-sub">Issued {fmtDate(e.issued)}</div>
                  </div>
                  <div className="row-aside">
                    <span
                      className={
                        'pill ' +
                        (e.status === 'Approved'
                          ? 'completed'
                          : e.status === 'Sent'
                          ? 'scheduled'
                          : 'followup')
                      }
                    >
                      <span className="dot" />
                      {e.status}
                    </span>
                  </div>
                </div>
              ))}
              {linkedEstimates.length === 0 && (
                <div className="empty-state">No estimates yet.</div>
              )}
            </div>
          </div>
          <div className="card">
            <div className="card-title">
              <span>Invoices</span>
              <button className="btn ghost sm">{Icon.plus({ size: 12 })} New</button>
            </div>
            <div className="row-list">
              {linkedInvoices.map((i) => (
                <div key={i.id} className="row">
                  <div className="mono" style={{ fontSize: 12 }}>
                    {i.id}
                  </div>
                  <div className="row-main">
                    <div className="row-title">{fmtMoney(i.total)}</div>
                    <div className="row-sub">Due {fmtDate(i.due)}</div>
                  </div>
                  <div className="row-aside">
                    <span
                      className={
                        'pill ' +
                        (i.status === 'Paid'
                          ? 'completed'
                          : i.status === 'Overdue'
                          ? 'cancel'
                          : 'scheduled')
                      }
                    >
                      <span className="dot" />
                      {i.status}
                    </span>
                  </div>
                </div>
              ))}
              {linkedInvoices.length === 0 && (
                <div className="empty-state">No invoices yet.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
